"use client";

import * as React from "react";
import { useTranslation } from "react-i18next";
import { Info, Pencil, Loader2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Separator } from "../../ui/separator";
import { MinimalEditor, PaneRecordings } from "./MinimalEditor";
import { RecordingPlayer } from "../../recording/RecordingPlayer";
import { ImageViewer } from "../../images/ImageViewer";
import { RecordingData } from "../../../hooks/useMediaRecorder";
import { MyContext } from "../MyContextProvider";
import {
  getPresignedUploadUrlForPrescription,
  uploadRecordingToR2,
  saveRecordingMetadataForPrescription,
  deleteRecordingFromPrescription,
  updateAutoPlaySettingsForPrescription,
} from "../../../api/prescriptionRecordings";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { toast } from "sonner";
import {
  AutoPlaySettings,
  AutoPlayConfig,
} from "../../recording/AutoPlaySettings";

// Type for additional info data - supports both legacy string and new object format
interface AdditionalInfoData {
  text: string;
  recordings?: {
    audio?: RecordingData;
    video?: RecordingData;
    image?: import("../../../api/processImages").ImageData;
  };
}

type Data = {
  metadata?: { additionalInfo: string | AdditionalInfoData };
  // Allow other fields without enforcing a strict shape
  [key: string]: any;
};

interface InfoPopoverProps {
  data: Data;
  setData: React.Dispatch<React.SetStateAction<Data>>;
  nodeId: any;
}

// Helper to check if HTML content is empty
const hasHtmlContent = (html: string): boolean => {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length > 0;
};

// Helper to check if recordings or image exist
const hasRecordings = (
  recordings?: {
    audio?: RecordingData;
    video?: RecordingData;
    image?: import("../../../api/processImages").ImageData;
  } | null
): boolean => {
  return !!(recordings?.audio || recordings?.video || recordings?.image);
};

// Helper to extract text from additionalInfo (handles both string and object formats)
const getInfoText = (info: string | AdditionalInfoData | undefined): string => {
  if (!info) return "";
  if (typeof info === "string") return info;
  return info.text || "";
};

// Helper to extract recordings from additionalInfo
const getInfoRecordings = (
  info: string | AdditionalInfoData | undefined
): PaneRecordings | undefined => {
  if (!info || typeof info === "string") return undefined;
  return info.recordings;
};

export function InfoPopover({ data, setData, nodeId }: InfoPopoverProps) {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [localText, setLocalText] = React.useState<string>("");
  const [localRecordings, setLocalRecordings] = React.useState<PaneRecordings>(
    {}
  );
  const [originalRecordings, setOriginalRecordings] =
    React.useState<PaneRecordings>({});
  const [isSaving, setIsSaving] = React.useState(false);
  const titleId = React.useId();

  // Auto-play settings state
  const [audioAutoPlay, setAudioAutoPlay] = React.useState<AutoPlayConfig>({
    enabled: false,
    mode: "single",
  });
  const [videoAutoPlay, setVideoAutoPlay] = React.useState<AutoPlayConfig>({
    enabled: false,
    mode: "single",
  });

  const { prescriptionId } = React.useContext(MyContext);
  const retryFetch = useRetryFetch();

  const currentInfo = data?.[nodeId]?.metadata?.additionalInfo;
  const currentText = getInfoText(currentInfo);
  const currentRecordings = getInfoRecordings(currentInfo);
  const hasText = hasHtmlContent(currentText);
  const hasAnyRecordings = hasRecordings(currentRecordings);

  const startEditing = () => {
    const existingRecordings = getInfoRecordings(currentInfo) || {};
    setLocalText(getInfoText(currentInfo));
    setLocalRecordings(existingRecordings);
    setOriginalRecordings(existingRecordings);

    // Load auto-play settings from recordings
    setAudioAutoPlay(
      existingRecordings?.audio?.autoPlay || { enabled: false, mode: "single" }
    );
    setVideoAutoPlay(
      existingRecordings?.video?.autoPlay || { enabled: false, mode: "single" }
    );

    setPopoverOpen(false);
    setDialogOpen(true);
  };

  const onCancel = () => {
    // Revoke any blob URLs for new recordings that weren't saved
    if (localRecordings?.audio?.blobUrl && !localRecordings?.audio?.key) {
      URL.revokeObjectURL(localRecordings.audio.blobUrl);
    }
    if (localRecordings?.video?.blobUrl && !localRecordings?.video?.key) {
      URL.revokeObjectURL(localRecordings.video.blobUrl);
    }
    setDialogOpen(false);
  };

  // Helper to upload a recording to R2
  const uploadRecording = async (
    type: "audio" | "video",
    recording: RecordingData
  ): Promise<RecordingData> => {
    if (!prescriptionId) {
      throw new Error("Prescription must be saved before adding recordings");
    }

    if (!recording.blobUrl) {
      throw new Error("Recording has no blob URL to upload");
    }

    // Get presigned URL
    const { uploadUrl, key } = await getPresignedUploadUrlForPrescription(
      prescriptionId,
      type,
      nodeId,
      retryFetch
    );

    // Fetch blob from local URL and upload
    const blob = await fetch(recording.blobUrl).then((r) => r.blob());
    await uploadRecordingToR2(uploadUrl, blob, recording.mimeType);

    // Save metadata to backend
    const { recording: savedRecording } =
      await saveRecordingMetadataForPrescription(
        prescriptionId,
        {
          key,
          type,
          activityId: nodeId,
          mimeType: recording.mimeType,
          duration: recording.duration,
          size: blob.size,
        },
        retryFetch
      );

    // Revoke the blob URL since we now have a cloud URL
    URL.revokeObjectURL(recording.blobUrl);

    return {
      ...recording,
      url: savedRecording.url,
      key: savedRecording.key,
    };
  };

  const onSave = async () => {
    setIsSaving(true);

    try {
      let finalRecordings = { ...localRecordings };

      // Delete any recordings that were removed (existed in original but not in local)
      if (prescriptionId) {
        // Check if audio was deleted
        if (originalRecordings?.audio?.key && !localRecordings?.audio) {
          try {
            await deleteRecordingFromPrescription(
              prescriptionId,
              "audio",
              nodeId,
              retryFetch
            );
          } catch (error) {
            console.error("Failed to delete audio from cloud:", error);
            // Continue anyway - the recording will be orphaned but save can proceed
          }
        }
        // Check if video was deleted
        if (originalRecordings?.video?.key && !localRecordings?.video) {
          try {
            await deleteRecordingFromPrescription(
              prescriptionId,
              "video",
              nodeId,
              retryFetch
            );
          } catch (error) {
            console.error("Failed to delete video from cloud:", error);
            // Continue anyway
          }
        }
        // Check if image was deleted
        if (originalRecordings?.image?.key && !localRecordings?.image) {
          try {
            const { deleteImageFromPrescription } = await import(
              "../../../api/prescriptionImages"
            );
            await deleteImageFromPrescription(
              prescriptionId,
              nodeId,
              retryFetch
            );
          } catch (error) {
            console.error("Failed to delete image from cloud:", error);
          }
        }
      }

      // Upload any new recordings (those with blobUrl but no key) when prescription exists
      // When !prescriptionId, keep blob-only recordings so they are persisted and uploaded on first save
      if (prescriptionId) {
        if (localRecordings?.audio?.blobUrl && !localRecordings?.audio?.key) {
          finalRecordings.audio = await uploadRecording(
            "audio",
            localRecordings.audio
          );
        }
        if (localRecordings?.video?.blobUrl && !localRecordings?.video?.key) {
          finalRecordings.video = await uploadRecording(
            "video",
            localRecordings.video
          );
        }
      } else {
        if (localRecordings?.audio?.blobUrl && !localRecordings?.audio?.key) {
          finalRecordings.audio = localRecordings.audio;
        }
        if (localRecordings?.video?.blobUrl && !localRecordings?.video?.key) {
          finalRecordings.video = localRecordings.video;
        }
      }

      // Update auto-play settings for existing recordings
      if (prescriptionId) {
        // Update audio auto-play if recording exists
        if (finalRecordings?.audio?.key) {
          try {
            await updateAutoPlaySettingsForPrescription(
              prescriptionId,
              { type: "audio", activityId: nodeId, autoPlay: audioAutoPlay },
              retryFetch
            );
            finalRecordings.audio = {
              ...finalRecordings.audio,
              autoPlay: audioAutoPlay,
            };
          } catch (error) {
            console.error("Failed to update audio auto-play settings:", error);
          }
        }

        // Update video auto-play if recording exists
        if (finalRecordings?.video?.key) {
          try {
            await updateAutoPlaySettingsForPrescription(
              prescriptionId,
              { type: "video", activityId: nodeId, autoPlay: videoAutoPlay },
              retryFetch
            );
            finalRecordings.video = {
              ...finalRecordings.video,
              autoPlay: videoAutoPlay,
            };
          } catch (error) {
            console.error("Failed to update video auto-play settings:", error);
          }
        }
      }

      const hasLocalRecordings =
        finalRecordings?.audio ||
        finalRecordings?.video ||
        finalRecordings?.image;

      setData((prev) => ({
        ...prev,
        [nodeId]: {
          ...prev[nodeId],
          metadata: {
            ...prev[nodeId]?.metadata,
            additionalInfo: hasLocalRecordings
              ? { text: localText, recordings: finalRecordings }
              : localText, // Keep as string if no recordings (backward compatible)
          },
        },
      }));
      setDialogOpen(false);
    } catch (error) {
      console.error("Failed to save recordings:", error);
      toast.error("Failed to save recordings. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecordingsChange = (recordings: PaneRecordings) => {
    setLocalRecordings(recordings);
  };

  return (
    <>
      {/* Popover for view mode */}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger>
          <Info className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">Additional info</span>
        </PopoverTrigger>

        <PopoverContent
          className="w-96 p-4"
          align="start"
          sideOffset={7}
          aria-labelledby={titleId}
          side="right"
        >
          <div className="space-y-3">
            {/* Title */}
            <div className="flex items-center justify-between">
              <h3 id={titleId} className="text-sm font-medium text-foreground">
                Additional Info
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={startEditing}
                aria-label="Edit additional info"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            <Separator />

            {/* View content */}
            <div className="space-y-3">
              {hasText ? (
                <div
                  className="prose prose-sm max-w-none text-sm text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: currentText }}
                />
              ) : !hasAnyRecordings ? (
                <div className="rounded-md border bg-muted/30 p-3">
                  <p className="text-sm text-muted-foreground">
                    No additional info yet.
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Click the pencil to add context, notes, or links.
                  </p>
                </div>
              ) : null}

              {/* Display recordings and image in view mode */}
              {hasAnyRecordings && (
                <div className="space-y-2">
                  {currentRecordings?.audio && (
                    <RecordingPlayer
                      recording={currentRecordings.audio}
                      type="audio"
                      compact
                      readOnly
                    />
                  )}
                  {currentRecordings?.video && (
                    <RecordingPlayer
                      recording={currentRecordings.video}
                      type="video"
                      compact
                      readOnly
                    />
                  )}
                  {currentRecordings?.image && (
                    <ImageViewer
                      image={currentRecordings.image}
                      compact
                      readOnly
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Dialog for edit mode - completely controlled, only closes via Cancel/Save buttons */}
      <Dialog open={dialogOpen} modal={true}>
        <DialogContent
          className="max-w-lg"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          // Hide the default X close button since we want only Cancel/Save to work
          hideClose
        >
          <DialogHeader>
            <DialogTitle>Edit Additional Info</DialogTitle>
          </DialogHeader>

          <div className="py-2 space-y-4">
            <MinimalEditor
              value={localText}
              onChange={setLocalText}
              placeholder="Add context, notes, or links..."
              recordings={localRecordings}
              onRecordingsChange={handleRecordingsChange}
              recordingContext="additionalInfo"
              processId={prescriptionId}
              activityId={nodeId}
            />

            {/* Auto-play settings for recordings */}
            {(localRecordings?.audio || localRecordings?.video) && (
              <div className="pt-3 border-t space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">
                  {t("autoplay_settings", "Auto-play Settings")}
                </h4>

                {localRecordings?.audio && (
                  <div className="pl-2 pb-2 border-l-2 border-primary/20">
                    <p className="text-xs font-medium text-primary mb-2">
                      Audio Recording
                    </p>
                    <AutoPlaySettings
                      config={audioAutoPlay}
                      onChange={setAudioAutoPlay}
                      compact
                    />
                  </div>
                )}

                {localRecordings?.video && (
                  <div className="pl-2 pb-2 border-l-2 border-purple-200">
                    <p className="text-xs font-medium text-purple-600 mb-2">
                      Video Recording
                    </p>
                    <AutoPlaySettings
                      config={videoAutoPlay}
                      onChange={setVideoAutoPlay}
                      compact
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
